import { createLazyHandleClient, } from "./lazy-handle-client.js";
export function createFlowCommandHandleController({ getHandleIdFromSettlement, isStaleHandleError, onClearResolvedHandle, }) {
    const handleClient = createLazyHandleClient({
        getHandleIdFromSettlement,
    });
    const notifyResolvedHandleCleared = (handleId) => {
        try {
            onClearResolvedHandle?.(handleId);
        }
        catch {
            // Observers cannot take ownership of the completed handle transition.
        }
    };
    const clearResolvedHandle = () => {
        const previousHandleId = handleClient.getHandleId();
        if (!previousHandleId) {
            return undefined;
        }
        handleClient.reset();
        notifyResolvedHandleCleared(previousHandleId);
        return previousHandleId;
    };
    const clearResolvedHandleForRetry = () => {
        const previousHandleId = handleClient.clearHandleForRetry();
        if (!previousHandleId) {
            return undefined;
        }
        notifyResolvedHandleCleared(previousHandleId);
        return previousHandleId;
    };
    const reset = () => {
        const previousHandleId = handleClient.getHandleId();
        handleClient.reset();
        if (previousHandleId) {
            notifyResolvedHandleCleared(previousHandleId);
        }
    };
    const runCommand = async (buildPayload, dispatchAndWait) => {
        return handleClient.runCommandWithRetry(buildPayload, dispatchAndWait, {
            shouldRetry: isStaleHandleError,
            onBeforeRetry: () => {
                clearResolvedHandleForRetry();
            },
        });
    };
    const runResolvedHandleCommand = async (buildPayload, dispatchAndWait) => {
        try {
            await handleClient.runResolvedHandleCommand(buildPayload, dispatchAndWait);
        }
        catch (error) {
            if (isStaleHandleError(error)) {
                clearResolvedHandle();
            }
            throw error;
        }
    };
    const invalidateResolvedHandle = (handleId) => {
        const invalidatedHandleId = handleClient.recordHandleInvalidation(handleId);
        if (!invalidatedHandleId) {
            return false;
        }
        notifyResolvedHandleCleared(invalidatedHandleId);
        return true;
    };
    return {
        runCommand,
        runResolvedHandleCommand,
        getHandleId: () => handleClient.getHandleId(),
        hasHandleId: (handleId) => handleClient.getHandleId() === handleId,
        invalidateResolvedHandle,
        hasPendingCommands: () => handleClient.hasPendingCommands(),
        subscribeHandle: (subscriber) => handleClient.subscribeHandle(subscriber),
        clearResolvedHandle,
        reset,
    };
}
